import { Resources, useResource } from "Infrastructure/Translation/Resources";
import dayjs from "dayjs";
import calendar from "dayjs/plugin/calendar";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useTranslation } from "react-i18next";
import "dayjs/locale/cs";
import "dayjs/locale/en";
import localeData from "dayjs/plugin/localeData";
import updateLocale from "dayjs/plugin/updateLocale";
import weekday from "dayjs/plugin/weekday";

enum WeekDays {
	Sunday = 0,
	Monday = 1,
	Tuesday = 2,
	Wednesday = 3,
	Thursday = 4,
	Friday = 5,
	Saturday = 6,
}

const dateResources = Resources.base.utilities.calendar;

const formats = {
	date: {
		default: "DD.MM.YYYY",
		CZ: "DD.MM.YYYY",
		GB: "DD/MM/YYYY",
		US: "MM/DD/YYYY",
	},
	dateTime: {
		default: "DD.MM.YYYY HH:mm",
		CZ: "DD.MM.YYYY HH:mm",
		GB: "DD/MM/YYYY HH:mm",
		US: "MM/DD/YYYY hh:mm A",
	},
	time: {
		default: "HH:mm",
		CZ: "HH:mm",
		GB: "HH:mm",
		US: "hh:mm A",
	},
	isoStringLocalTime: {
		// Format for ISO string with local time, not UTC +0
		default: "YYYY-MM-DDTHH:mm:ss",
	},
};
dayjs.extend(calendar);
dayjs.extend(localizedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(updateLocale);

export const useDateFormat = () => {
	const { t } = useResource();
	const { i18n } = useTranslation();
	const currentCountry = i18n.language.split("-")[1];
	const currentLang = i18n.language.split("-")[0];

	dayjs.locale(i18n.language);
	dayjs.updateLocale(currentLang, {
		weekStart: WeekDays.Monday,
	});

	const getFormat = (type: keyof typeof formats) =>
		formats[type][currentCountry] || formats[type].default;

	const format = (
		date: string | Date | dayjs.Dayjs,
		type: keyof typeof formats,
	): string => dayjs(date).format(getFormat(type));

	const formatCalendar = (date: string | Date | dayjs.Dayjs): string =>
		dayjs(date).calendar(null, {
			sameDay: `[${t(dateResources.todayAt)}] ${getFormat("time")}`,
			nextDay: `[${t(dateResources.tomorrowAt)}] ${getFormat("time")}`,
			nextWeek: `[${t(dateResources.nextWeekDayAt)}] dddd [${t(dateResources.at)}] ${getFormat("time")}`,
			lastDay: `[${t(dateResources.yesterdayAt)}] ${getFormat("time")}`,
			lastWeek: `[${t(dateResources.lastWeekDayAt)}] dddd [${t(dateResources.at)}] ${getFormat("time")}`,
			sameElse: getFormat("dateTime"),
		});

	return {
		dateFormat: getFormat("date"),
		dateTimeFormat: getFormat("dateTime"),
		formatDate: (date: string | Date | dayjs.Dayjs) => format(date, "date"),
		formatDateTime: (date: string | Date | dayjs.Dayjs) =>
			format(date, "dateTime"),
		formatCalendar,
		format,
	};
};
